import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Loader2, MousePointerClick, FileText, Zap, Clock, Route } from "lucide-react";

const PAGE_NAMES = [
  ["/toursignup", "Public Tour Sign-Up"], ["/tournaments", "Tournaments Hub"], ["/court-board", "Court Board"], ["/brackets", "Brackets"],
  ["/store", "The Store"], ["/shops", "Merchant Shops"], ["/profile", "Player Profile"], ["/book", "Book a Session"], ["/lda", "LDA Training Hub"],
  ["/league", "BPL League"], ["/community", "Community"], ["/cards", "Card Vault"], ["/vault", "Card Vault"], ["/wallet", "Wallet / Bullion"],
  ["/admin", "Admin Console"], ["/live", "Bullpen Live"], ["/scorekeeper", "Scorekeeper"], ["/pbr", "PBR Rankings"], ["/tour", "Tour Standings"],
  ["/notifications", "Notifications"], ["/auth", "Sign In"], ["/", "Home"],
];
export const pageName = (path) => {
  if (!path) return "";
  const p = path.split("?")[0];
  const hit = PAGE_NAMES.find(([k]) => (k === "/" ? p === "/" : p === k || p.startsWith(k + "/")));
  return hit ? hit[1] : p;
};
const humanize = (s) => String(s || "").replace(/[-_]+/g, " ").replace(/\b(btn|button)\b/gi, "").trim();
const fmtDwell = (s) => (s == null ? "" : s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`);
const t = (iso) => new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
const d = (iso) => new Date(iso).toLocaleDateString([], { month: "short", day: "numeric" });

export default function JourneyPanel({ userId, name, testid = "journey" }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [hours, setHours] = useState(48);
  useEffect(() => {
    setData(null); setErr(null);
    api.get("/admin/journey", { params: { user_id: userId || undefined, name, hours } })
      .then(({ data }) => setData(data))
      .catch((e) => setErr(e?.response?.data?.detail || "Could not load this member's journey"));
  }, [userId, name, hours]);

  if (err) return <div className="px-3 py-3 text-xs text-amber-400" data-testid={`${testid}-error`}>{err}</div>;
  if (!data) return <div className="px-3 py-4 text-xs text-zinc-500 flex items-center gap-2" data-testid={`${testid}-loading`}><Loader2 className="w-3.5 h-3.5 animate-spin" /> Tracing {name}'s path through the site…</div>;

  return (
    <div className="border border-pb-cyan/30 bg-black/50 p-3 mt-2 animate-fade-up" data-testid={testid} onClick={(e) => e.stopPropagation()}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.25em] text-pb-cyan"><Route className="w-3.5 h-3.5" /> Member Journey — {data.user.name}</div>
        <div className="flex items-center gap-2 text-[10px] text-zinc-500">
          <span data-testid={`${testid}-today`}><Clock className="w-3 h-3 inline mr-1 text-amber-400" />{data.today_minutes}m on site today</span>
          <span className="text-zinc-700">·</span>
          <span data-testid={`${testid}-steps`}>{data.total_steps} tracked step{data.total_steps === 1 ? "" : "s"}</span>
          <select data-testid={`${testid}-range`} value={hours} onChange={(e) => setHours(Number(e.target.value))}
            className="bg-pb-elevated border border-pb-border px-1.5 py-0.5 text-[10px] text-white focus:outline-none">
            <option value={24}>last 24h</option><option value={48}>last 48h</option><option value={168}>last 7 days</option><option value={720}>last 30 days</option>
          </select>
        </div>
      </div>
      <div className="text-[10px] text-zinc-500 mb-2">{data.user.email}{data.user.phone ? ` · ${data.user.phone}` : ""}</div>
      {data.sessions.length === 0 && (
        <div className="text-xs text-zinc-600 py-2" data-testid={`${testid}-empty`}>No page-by-page movement recorded in this window yet — tracking starts the moment this member loads a page while logged in.</div>
      )}
      <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
        {data.sessions.map((s, si) => (
          <div key={si} className="border border-pb-border/60" data-testid={`${testid}-session-${si}`}>
            <div className="flex flex-wrap items-center gap-3 px-2.5 py-1.5 bg-pb-elevated/60 text-[10px] uppercase tracking-widest">
              <span className="font-black text-white">{d(s.started_at)} · {t(s.started_at)} → {t(s.ended_at)}</span>
              <span className="text-amber-400">{s.minutes}m</span>
              <span className="text-pb-cyan">{s.pages} page{s.pages === 1 ? "" : "s"}</span>
              <span className="text-zinc-400">{s.clicks} click{s.clicks === 1 ? "" : "s"}</span>
              {si === 0 && <span className="ml-auto text-[#A3E635]">latest</span>}
            </div>
            <div className="divide-y divide-pb-border/30">
              {s.steps.map((st, i) => (
                <div key={i} className="flex items-start gap-2 px-2.5 py-1.5 text-xs" data-testid={`${testid}-step-${si}-${i}`}>
                  <span className="font-mono text-[10px] text-zinc-600 w-16 shrink-0 pt-0.5">{t(st.at)}</span>
                  {st.kind === "page" && (
                    <>
                      <FileText className="w-3.5 h-3.5 text-pb-cyan shrink-0 mt-0.5" />
                      <span className="flex-1 min-w-0"><span className="text-white font-semibold">Opened {pageName(st.path)}</span> <span className="text-zinc-600 font-mono text-[10px]">{st.path}</span></span>
                      {st.dwell_s != null && <span className="text-[10px] text-amber-400 font-mono shrink-0">{fmtDwell(st.dwell_s)}</span>}
                    </>
                  )}
                  {st.kind === "click" && (
                    <>
                      <MousePointerClick className="w-3.5 h-3.5 text-zinc-400 shrink-0 mt-0.5" />
                      <span className="flex-1 min-w-0 text-zinc-300">Clicked <span className="text-white">{st.text || humanize(st.label)}</span>{st.text && st.label && st.label !== "button" && st.label !== "a" ? <span className="text-zinc-600 font-mono text-[10px]"> · {humanize(st.label)}</span> : null}<span className="text-zinc-600 text-[10px]"> on {pageName(st.path)}</span></span>
                    </>
                  )}
                  {st.kind === "event" && (
                    <>
                      <Zap className="w-3.5 h-3.5 text-[#A3E635] shrink-0 mt-0.5" />
                      <span className="flex-1 min-w-0 text-[#A3E635] font-semibold">{st.label}</span>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
